import type { Task } from '../../services/taskService';
import { isPlanningTask, orderedPlanningTasks, visiblePlanningTasks } from '../../utils/planningTaskPresentation';

/** How long a task that just finished stays in the dock, marked done, before it is hidden. */
export const COMPLETION_GRACE_MS = 10000;

const ACTIVE = new Set(['queued', 'ai_in_progress', 'human_in_progress']);

/**
 * What the dock knows about each task it has followed: `0` while active, `1`
 * during the grace period after it left the active set, and `-1` once that
 * grace has passed and the task has settled into the recently-done list.
 */
export type CompletionFollow = Readonly<Record<string, number>>;

export const isActivePlanningTask = (task: Task): boolean => (
  isPlanningTask(task) && ACTIVE.has(task.status)
  && task.planning_metadata?.awaiting_confirmation !== true
);

/** Advance the follow record: new active tasks join and finished ones enter their grace. */
export const followCompletion = (follow: CompletionFollow, activeIds: readonly string[]): CompletionFollow => {
  const next: Record<string, number> = { ...follow };
  let changed = false;

  activeIds.forEach((id) => {
    if (next[id] !== 0) { next[id] = 0; changed = true; }
  });
  Object.keys(next).forEach((id) => {
    if (next[id] === 0 && !activeIds.includes(id)) { next[id] = 1; changed = true; }
  });

  return changed ? next : follow;
};

/** A task's grace ran out: it leaves the dock for the recently-done list. */
export const settleTask = (follow: CompletionFollow, id: string): CompletionFollow => (
  follow[id] === 1 ? { ...follow, [id]: -1 } : follow
);

export const graceTaskIds = (follow: CompletionFollow): string[] => (
  Object.keys(follow).filter((id) => follow[id] === 1)
);

export interface DockTasks {
  /** Running and queued tasks, then tasks still in their done grace, in plan order. */
  shown: Task[];
  /** Finished tasks of the same attempt that the dock has already hidden. */
  recent: Task[];
}

const finished = (task: Task): boolean => ['done', 'error', 'cancelled'].includes(task.status);

/**
 * Split a project's planning tasks into what the dock shows now and what it
 * tucks under the arrow. Tasks count as active only while the planning run is
 * reported live (`running`): a task left queued by an abandoned run is history,
 * not something to dock above the composer forever.
 */
// eslint-disable-next-line max-statements
export const dockTasks = (tasks: Task[], follow: CompletionFollow, running: boolean): DockTasks => {
  // A retry's own bookkeeping task names the action, not the work; the pages it reruns are the rows.
  const ordered = visiblePlanningTasks(orderedPlanningTasks(tasks.filter(isPlanningTask)))
    .filter((task) => task.planning_metadata?.artifact_type !== 'retry_attempt');
  const active = running ? ordered.filter(isActivePlanningTask) : [];
  const activeIds = new Set(active.map((task) => task.id));
  const inGrace = ordered.filter((task) => !activeIds.has(task.id) && follow[task.id] === 1);
  const attempts = new Set([...active, ...inGrace].map((task) => task.planning_metadata?.run_id).filter(Boolean));
  const recent = ordered.filter((task) => finished(task) && follow[task.id] !== 1
    && (follow[task.id] === -1 || attempts.has(task.planning_metadata?.run_id)));

  return { shown: [...active, ...inGrace], recent };
};
